'use client';

import { MapPin, Clock, Truck, Package, ArrowRight, ArrowLeft } from 'lucide-react';
import Link from 'next/link';
import { useState, useEffect } from 'react';
import { isStoreOpen } from '@/lib/storeStatus';

export default function RelayPointsPage() {
  const [storeOpen, setStoreOpen] = useState(false);

  useEffect(() => {
    setStoreOpen(isStoreOpen());
    // Mise à jour toutes les minutes
    const interval = setInterval(() => {
      setStoreOpen(isStoreOpen());
    }, 60000);
    return () => clearInterval(interval);
  }, []);
  const relayPartners = [
    {
      id: 1,
      name: 'Pickup Chronopost',
      logoSrc: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQLkDh3fk6ey4mjXbelKo-xdaOEqBXhRe7P7Q&s',
      description: 'Service de retrait et dépôt de colis La Poste',
      coverage: '15 000+ points relais',
      color: 'from-red-50 to-orange-100',
      features: [
        "Retrait jusqu'à 14 jours",
        'Suivi en temps réel',
        'Service rapide et fiable',
        'Réseau La Poste'
      ]
    },
    {
      id: 2,
      name: 'DPD',
      logoSrc: 'https://cdn.shopify.com/app-store/listing_images/f1636f1bb92febfb4469b13a97849de8/icon/CPTarLP0lu8CEAE=.png',
      description: 'Service de livraison rapide et fiable',
      coverage: '8 000+ points relais',
      color: 'from-red-50 to-red-100',
      features: [
        "Retrait jusqu'à 10 jours",
        'Livraison rapide',
        'Notification SMS',
        'Service premium'
      ]
    },
    {
      id: 3,
      name: 'Colissimo',
      logoSrc: 'https://cdn.shopify.com/app-store/listing_images/6b1969a70f0f38f4f0b655d3b1b1f9fb/icon/CKjWxJGB1vsCEAE=.png',
      description: 'Service de dépôt et retrait La Poste',
      coverage: '20 000+ points relais',
      color: 'from-yellow-50 to-yellow-100',
      features: [
        "Retrait jusqu'à 14 jours",
        'Réseau La Poste',
        'Suivi complet',
        'Service de confiance'
      ]
    },
    {
      id: 4,
      name: 'UPS',
      logoSrc: 'https://about.ups.com/content/dam/upsstories/images/newsroom/statements/ups-statement-on-u-s--driver-voluntary-program/UPSstatement-logo-1023x960.jpg',
      description: 'Service de livraison express international',
      coverage: '6 000+ points relais',
      color: 'from-amber-50 to-amber-100',
      features: [
        "Retrait jusqu'à 7 jours",
        'Livraison internationale',
        'Suivi en temps réel',
        'Service professionnel'
      ]
    },
    {
      id: 5,
      name: 'Mondial Relay',
      logoSrc: 'https://cdn.shopify.com/app-store/listing_images/1ce5021cbfb5eff03e8af1d8bbfed6b9/icon/COaP1rGF8_kCEAE=.png',
      description: 'Réseau de points relais en France et Europe',
      coverage: '12 000+ points relais',
      color: 'from-blue-50 to-blue-100',
      features: [
        "Retrait jusqu'à 14 jours",
        'Horaires étendus',
        'Livraison européenne',
        'Tarifs économiques'
      ]
    },
    {
      id: 6,
      name: 'Vinted Go',
      logoSrc: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcThODmV0QRONIToZ9jea-eFjiLgK0o-5Tk8Ww&s',
      description: 'Solution de livraison sécurisée pour occasion',
      coverage: '5 000+ points relais',
      color: 'from-green-50 to-green-100',
      features: [
        "Spécialiste de l'occasion",
        "Retrait jusqu'à 12 jours",
        'Emballage sécurisé',
        'Assurance incluse'
      ]
    }
  ];

  const businessHours = [
    { day: 'Lundi', hours: '10:00 - 19:00' },
    { day: 'Mardi', hours: '10:00 - 19:00' },
    { day: 'Mercredi', hours: '10:00 - 19:00' },
    { day: 'Jeudi', hours: '10:00 - 19:00' },
    { day: 'Vendredi', hours: '10:00 - 19:00' },
    { day: 'Samedi', hours: '10:00 - 19:00' },
    { day: 'Dimanche', hours: 'Fermé' }
  ];

  const relayServices = [
    {
      title: 'Dépôt de Colis',
      icon: Package,
      description: 'Déposez vos colis Chronopost, DPD, Colissimo, UPS, Mondial Relay et Vinted Go directement en boutique'
    },
    {
      title: 'Retrait de Colis',
      icon: Truck,
      description: 'Récupérez vos commandes en toute sécurité pendant nos horaires d\'ouverture'
    },
    {
      title: 'Envoi de Colis',
      icon: MapPin,
      description: 'Service d\'expédition disponible pour tous vos envois'
    }
  ];

  return (
    <main className="min-h-screen bg-white">
      {/* Hero Section */}
      <section aria-label="En-tête point relais" className="relative overflow-hidden bg-gradient-to-r from-[#23994F] to-green-600 py-16 sm:py-24">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full translate-x-1/2 translate-y-1/2"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            href="/"
            className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-8 transition-colors"
          >
            <ArrowLeft size={20} />
            <span>Retour à l'accueil</span>
          </Link>

          <div className="text-center mb-12">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
              Point Relais Verphone
            </h1>
            <p className="text-lg sm:text-xl text-white max-w-2xl mx-auto">
              Déposez ou récupérez vos colis via Chronopost, DPD, Colissimo, UPS, Mondial Relay ou Vinted Go. Profitez de notre service d'envoi de colis directement depuis la boutique Verphone.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-6 text-white text-center">
              <div className="mb-3 flex justify-center">
                <Package className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Dépôt</h3>
              <p className="text-white">Déposez vos colis facilement</p>
            </div>
            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-6 text-white text-center">
              <div className="mb-3 flex justify-center">
                <Truck className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Retrait</h3>
              <p className="text-white">Récupérez vos commandes</p>
            </div>
            <div className="bg-white/10 backdrop-blur border border-white/20 rounded-xl p-6 text-white text-center">
              <div className="mb-3 flex justify-center">
                <Clock className="w-8 h-8" />
              </div>
              <h3 className="text-2xl font-bold mb-2">Pratique</h3>
              <p className="text-white">Horaires flexibles 6j/7</p>
            </div>
          </div>
        </div>
      </section>


      <section className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 id="partners-heading" className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Nos Partenaires de Livraison
            </h2>
            <p className="text-lg text-gray-600">
              Choisissez parmi nos partenaires de confiance pour récupérer votre commande
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {relayPartners.map((partner) => (
              <div
                key={partner.id}
                className={`bg-gradient-to-br ${partner.color} rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-shadow`}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-16 h-16 rounded-xl bg-white/80 flex items-center justify-center overflow-hidden">
                    <img
                      src={partner.logoSrc}
                      alt={partner.name}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">{partner.name}</h3>
                    <p className="text-gray-700 mb-2">{partner.description}</p>
                    <div className="flex items-center gap-2 text-sm font-semibold text-[#23994F]">
                      <MapPin size={16} />
                      {partner.coverage}
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  {partner.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-[#23994F] rounded-full"></div>
                      <span className="text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <a
                  href={partner.id === 1 ? 'https://www.chronopost.fr/fr/relais-pickup' : 'https://vintedgo.com'}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={`En savoir plus sur ${partner.name}`}
                  className="mt-6 w-full py-3 bg-[#23994F] text-white font-semibold rounded-lg hover:bg-[#1a7840] transition-colors flex items-center justify-center gap-2 group"
                >
                  En savoir plus
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>


      <section aria-labelledby="services-heading" className="py-16 sm:py-20 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 id="services-heading" className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Services Point Relais
            </h2>
            <p className="text-lg text-gray-600">
              Tout ce que vous pouvez faire depuis notre boutique Verphone
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {relayServices.map((service, idx) => (
              <div
                key={idx}
                className="bg-white border-2 border-gray-200 rounded-xl p-8 text-center hover:shadow-lg hover:border-[#23994F] transition-all duration-300"
              >
                <div className="mb-6 flex justify-center">
                  <div className="w-16 h-16 bg-[#23994F]/10 rounded-full flex items-center justify-center">
                    <service.icon className="w-8 h-8 text-[#23994F]" />
                  </div>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      <section aria-labelledby="hours-heading" className="py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">

            <div>
              <h2 id="hours-heading" className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                Horaires Verphone
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Notre équipe est disponible pour répondre à toutes vos questions concernant votre commande, les points relais, ou toute information concernant nos produits reconditionnés.
              </p>
            </div>


            <div>
              <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-sm">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <Clock className="text-[#23994F]" size={24} />
                    Nos Horaires
                  </h3>
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${storeOpen ? 'bg-green-100' : 'bg-red-100'}`}>
                    <div className={`w-2.5 h-2.5 rounded-full ${storeOpen ? 'bg-green-500 animate-pulse' : 'bg-red-500'}`}></div>
                    <span className={`text-sm font-semibold ${storeOpen ? 'text-green-700' : 'text-red-700'}`}>
                      {storeOpen ? 'Ouvert' : 'Fermé'}
                    </span>
                  </div>
                </div>

                <div className="space-y-4">
                  {businessHours.map((item, idx) => (
                    <div
                      key={idx}
                      className={`flex justify-between items-center p-4 rounded-lg transition-colors ${
                        item.hours === 'Fermé'
                          ? 'bg-gray-100'
                          : idx < 5
                          ? 'bg-green-50 hover:bg-green-100'
                          : idx === 5
                          ? 'bg-yellow-50 hover:bg-yellow-100'
                          : 'bg-gray-100'
                      }`}
                    >
                      <span className={`font-semibold ${item.hours === 'Fermé' ? 'text-gray-500' : 'text-gray-900'}`}>
                        {item.day}
                      </span>
                      <span className={`font-semibold ${item.hours === 'Fermé' ? 'text-gray-500' : 'text-[#23994F]'}`}>
                        {item.hours}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-8 pt-8 border-t border-gray-200">
                  <p className="text-sm text-gray-600 mb-4">
                    Nous sommes fermés les jours fériés
                  </p>
                  <a
                    href="tel:0259164944"
                    className="block w-full py-3 bg-[#23994F] text-white font-semibold rounded-lg hover:bg-[#1a7840] transition-colors text-center"
                    aria-label="Appeler Verphone au 02 59 16 49 44"
                  >
                    Nous contacter
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>


      <section className="py-16 sm:py-20 bg-gradient-to-r from-[#23994F] to-green-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
            Prêt à commander?
          </h2>
          <p className="text-lg text-white mb-8">
            Découvrez nos produits reconditionnés et choisissez votre mode de livraison préféré
          </p>
          <Link
            href="/products"
            className="inline-block px-8 py-4 bg-white text-[#23994F] font-semibold rounded-lg hover:bg-gray-100 transition-colors text-lg"
            aria-label="Voir tous nos produits reconditionnés"
          >
            Voir nos produits
          </Link>
        </div>
      </section>
    </main>
  );
}

